import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, MessageSquare, Bell, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import Reveal from "@/components/Reveal";

const fmt = (d) => { if (!d) return "TBA"; try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }); } catch { return d; } };
const daysLeft = (d) => { if (!d) return null; const diff = Math.ceil((new Date(d + "T00:00:00") - new Date()) / 86400000); return diff; };

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [going, setGoing] = useState(false);
  const [comp, setComp] = useState(0);

  const load = () => api.get("/dashboard").then(({ data }) => { setData(data); setGoing((data.my_rsvps || []).includes(data.next_gathering?.id)); });
  useEffect(() => { load(); api.get("/profile/me").then(({ data }) => setComp(data.completion || 0)); }, []);

  const rsvp = async () => {
    const ev = data.next_gathering;
    try {
      if (going) { await api.delete(`/events/${ev.id}/rsvp`); toast("RSVP cancelled."); }
      else { await api.post(`/events/${ev.id}/rsvp`); toast.success("You're on the list!"); }
      load();
    } catch { toast.error("Something went wrong."); }
  };

  if (!data) return <div className="min-h-screen bg-mahogany-900" />;
  const cr = data.current_read;
  const ev = data.next_gathering;
  const dl = cr ? daysLeft(cr.reading_deadline) : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-mahogany-950 to-mahogany-900 pt-32 pb-20 px-6 grain" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto">
        <Reveal className="mb-12">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <Link to="/profile"><Avatar src={user?.picture} name={user?.display_name || user?.first_name} size={92} /></Link>
            <div className="flex-1">
              <p className="font-script text-3xl text-fuchsia mb-1">Welcome back,</p>
              <h1 className="font-serif text-4xl sm:text-5xl text-cream leading-none">{user?.first_name || user?.display_name || "friend"}.</h1>
              <p className="text-cream/50 mt-2 uppercase tracking-[0.2em] text-xs">Your private reading lounge</p>
            </div>
            <div className="sm:w-64">
              <div className="flex justify-between text-xs text-cream/50 mb-1"><span>Profile</span><span>{comp}% complete</span></div>
              <div className="h-1.5 bg-mahogany-700 rounded-full overflow-hidden mb-3"><div className="h-full bg-gold transition-all" style={{ width: `${comp}%` }} /></div>
              <div className="flex gap-2">
                <Link to="/profile/edit" className="btn-secondary !py-2 !px-4 !text-xs" data-testid="dash-edit-profile">Edit Profile</Link>
                <Link to={`/members/${user?.id}`} className="btn-secondary !py-2 !px-4 !text-xs" data-testid="dash-view-profile">View Profile</Link>
              </div>
            </div>
          </div>
          {comp < 60 && <p className="text-caramel/80 font-script text-2xl mt-4">Make yourself at home — tell the Room a little about your reading life.</p>}
        </Reveal>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Current read */}
          {cr && (
            <Reveal className="lg:col-span-2">
              <div className="bg-mahogany-950 gold-frame rounded-sm p-8 h-full" data-testid="dash-current-read">
                <p className="overline mb-5">Current Read</p>
                <div className="flex gap-6">
                  <img src={cr.cover_url} alt={cr.title} className="w-32 rounded-sm shadow-lg shrink-0" />
                  <div>
                    <h2 className="font-serif text-3xl text-cream leading-tight">{cr.title}</h2>
                    <p className="text-caramel mb-3">by {cr.author}</p>
                    <p className="text-cream/60 text-sm leading-relaxed line-clamp-3 mb-4">{cr.description}</p>
                    {dl !== null && (
                      <div className="mb-4">
                        <div className="flex justify-between text-xs text-cream/50 mb-1"><span>Reading Progress</span><span>{dl > 0 ? `${dl} days left` : "Discussion time!"}</span></div>
                        <div className="h-1.5 bg-mahogany-700 rounded-full overflow-hidden"><div className="h-full bg-fuchsia" style={{ width: `${Math.min(100, Math.max(10, 100 - dl * 3))}%` }} /></div>
                      </div>
                    )}
                    <Link to={`/books/${cr.id}/discussion`} className="btn-primary !py-2.5 !px-6" data-testid="dash-discussion-link">Join The Discussion</Link>
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          {/* Next gathering */}
          <Reveal delay={0.05}>
            <div className="bg-gradient-to-br from-fuchsia/20 to-mahogany-950 gold-frame rounded-sm p-8 h-full" data-testid="dash-next-gathering">
              <p className="overline mb-5">Next Gathering</p>
              {ev ? (
                <>
                  <span className="text-gold text-xs uppercase tracking-wider">{ev.type}</span>
                  <h2 className="font-serif text-2xl text-cream mt-1 mb-4">{ev.title}</h2>
                  <p className="flex items-center gap-2 text-cream/70 text-sm mb-1.5"><Calendar size={14} className="text-gold" /> {fmt(ev.date)} · {ev.time}</p>
                  <p className="flex items-center gap-2 text-cream/70 text-sm mb-6"><MapPin size={14} className="text-gold" /> {ev.location}</p>
                  <button onClick={rsvp} className={going ? "btn-secondary w-full !py-2.5" : "btn-primary w-full !py-2.5"} data-testid="dash-rsvp">
                    {going ? <span className="flex items-center gap-2 justify-center"><Check size={16} /> Going</span> : "RSVP Now"}
                  </button>
                </>
              ) : <p className="text-cream/50 text-sm">No gatherings scheduled yet.</p>}
            </div>
          </Reveal>

          {/* Announcement */}
          <Reveal delay={0.1}>
            <div className="bg-mahogany-800/50 gold-frame rounded-sm p-8 h-full" data-testid="dash-announcement">
              <p className="overline mb-4 flex items-center gap-2"><Bell size={13} /> From The Room</p>
              {data.announcement ? (
                <>
                  <h3 className="font-serif text-2xl text-cream mb-3">{data.announcement.title}</h3>
                  <p className="text-cream/60 text-sm leading-relaxed line-clamp-6">{data.announcement.body}</p>
                </>
              ) : <p className="text-cream/50 text-sm">No announcements yet.</p>}
            </div>
          </Reveal>

          {/* Discussion of the month */}
          <Reveal delay={0.15} className="lg:col-span-2">
            <div className="bg-mahogany-950 gold-frame rounded-sm p-8 h-full flex flex-col" data-testid="dash-prompt">
              <p className="overline mb-4 flex items-center gap-2"><MessageSquare size={13} /> Discussion of the Month</p>
              {data.prompt ? (
                <>
                  <p className="font-serif text-2xl text-cream leading-snug mb-2">"{data.prompt.question}"</p>
                  <p className="text-caramel text-sm mb-6">{data.prompt.month}</p>
                  {cr && <Link to={`/books/${cr.id}/discussion`} className="inline-flex items-center gap-2 text-fuchsia uppercase tracking-wider text-sm mt-auto hover:gap-3 transition-all" data-testid="dash-prompt-link">Share your thoughts <ArrowRight size={15} /></Link>}
                </>
              ) : <p className="text-cream/50 text-sm">No prompt this month yet.</p>}
            </div>
          </Reveal>
        </div>

        {/* Upcoming reads */}
        {data.upcoming_reads?.length > 0 && (
          <Reveal delay={0.2} className="mt-10">
            <p className="overline mb-6">Upcoming Reads</p>
            <div className="flex gap-6 flex-wrap">
              {data.upcoming_reads.map((b) => (
                <Link to={`/books/${b.id}/discussion`} key={b.id} className="w-28 group" data-testid={`dash-upcoming-${b.id}`}>
                  <img src={b.cover_url} alt={b.title} className="w-full rounded-sm shadow-lg transition-transform group-hover:-translate-y-1" />
                  <p className="text-cream/80 text-xs mt-2 leading-tight">{b.title}</p>
                </Link>
              ))}
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
}
